import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import * as SelectPrimitive from "@radix-ui/react-select";
import {
  Mic,
  Square,
  Trash2,
  Save,
  AlertTriangle,
  Loader2,
  Shuffle,
  ArrowUpRight,
  AudioLines,
  ServerCog,
  RefreshCw,
  Check,
  History,
  X,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Info,
  RotateCcw,
  Pause,
  Play,
  Zap,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";
import { useSpeechRecorder, formatDuration, isSpeechRecognitionSupported } from "@/lib/speech";
import { useAudioRecorder, isMediaRecorderSupported } from "@/lib/audio";
import { Waveform } from "@/components/Waveform";
import { InfoPopover } from "@/components/InfoPopover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useUnsaved } from "@/context/UnsavedContext";
import { useCategories } from "@/context/CategoriesContext";
import { useGame } from "@/components/game/useGame";
import { nextQuestionSet } from "@/lib/questionRotation";
import { SeasonKickoff } from "@/components/game/SeasonKickoff";
import { WeekStanding } from "@/components/game/Standings";
import { StreakChip, StarterCard, SaveReward, ScoringHint } from "@/components/game/PersonalGame";
import { ChallengeNotices } from "@/components/game/Challenges";
import { KeywordNotices } from "@/components/keywords/KeywordNotices";
import { SavedScopeCard } from "@/components/ScopeChip";
import { SiteGapsLine } from "@/components/SiteGapsLine";
import { Link } from "react-router-dom";
import { functieLabel, isForMe, markQuestionAnswered, pickStarterQuestion, starterMode, todayKey } from "@/lib/starter";

// Round U — a question written for another functie: anyone may answer it, the label says who it is for.
const FunctieLabel = ({ q, title }) => {
  const label = functieLabel(q, title);
  if (!label) return null;
  return (
    <span data-testid={`question-functie-label-${q.id}`}
      className="block mt-1 font-mono text-[9px] tracking-[0.15em] uppercase text-tw-muted break-words">
      {label}
    </span>
  );
};

// Max recording length (server/Whisper path). Warn near the end, hard-stop at 60 min.
const MAX_RECORD_SEC = 60 * 60;

// Round N — focusvraag van het lopende seizoen: dubbele punten.
const FocusTag = ({ id }) => (
  <span data-testid={`question-focus-tag-${id}`} title="Focuscategorie van dit seizoen: dubbele punten"
    className="ml-2 inline-flex items-center gap-1 bg-tw-yellow text-black px-1.5 py-0.5 font-mono text-[9px] font-bold tracking-[0.15em] uppercase align-middle">
    <Zap size={10} strokeWidth={3} /> Telt dubbel
  </span>
);
const WARN_RECORD_SEC = 55 * 60;
// Recordings larger than this likely take more than a few seconds → show the long-wait copy.
const LONG_AUDIO_BYTES = 6 * 1024 * 1024;


export const TRANSFORM_LABELS = {
  raw: "Alleen transcript",
  summary: "Samenvatting",
  bullets: "Bulletpoints",
  sop: "Werkinstructie (SOP)",
  article: "Kennisartikel",
};
const TRANSFORM_ORDER = ["raw", "summary", "bullets", "sop", "article"];

function greetingNl() {
  const h = new Date().getHours();
  if (h < 5) return "Goedenavond";
  if (h < 12) return "Goedemorgen";
  if (h < 18) return "Goedemiddag";
  return "Goedenavond";
}

// ---------- Web Speech environment detection ----------
// iOS Safari and iOS webviews expose webkitSpeechRecognition but return only fragments
// in the field. Detect them so we never route users to the broken path by default.
function isIOSLike() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const isIPad = /iPad|Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
  return /iPhone|iPod|iPad/.test(platform) || /iPhone|iPod|iPad/i.test(ua) || isIPad;
}
function isBrokenWebSpeechEnv() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // In-app browsers (Instagram, FB, LinkedIn, WeChat, Line) and generic webviews.
  return /(FBAN|FBAV|Instagram|LinkedInApp|Line|MicroMessenger|; wv\)|WebView)/i.test(ua);
}

// ---------- Concept-herstel (draft auto-save) ----------
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h
const DRAFT_SAVE_INTERVAL_MS = 3000;

function draftKey(userId) {
  return `tw1_draft_${userId}`;
}

function safeWriteDraft(userId, payload) {
  if (!userId) return;
  try {
    localStorage.setItem(draftKey(userId), JSON.stringify(payload));
  } catch {
    // Full/unavailable localStorage — fail silent, never block the page.
  }
}

function safeReadDraft(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(draftKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !parsed.savedAt) return null;
    if (Date.now() - new Date(parsed.savedAt).getTime() > DRAFT_MAX_AGE_MS) {
      try { localStorage.removeItem(draftKey(userId)); } catch { /* noop */ }
      return null;
    }
    if (!parsed.transcript || !String(parsed.transcript).trim()) return null;
    return parsed;
  } catch {
    return null;
  }
}

function safeClearDraft(userId) {
  if (!userId) return;
  try { localStorage.removeItem(draftKey(userId)); } catch { /* noop */ }
}

function draftRelativeNl(savedAt) {
  try {
    const diffMs = Date.now() - new Date(savedAt).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "zojuist";
    if (mins < 60) return `${mins} min geleden`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} uur geleden`;
    return "meer dan een dag geleden";
  } catch {
    return "onlangs";
  }
}

// B1.3 — same title-derivation rule as Mijn kluis, used only for the redo banner text.
function deriveTitleFallback(r) {
  if (r.auto_title && r.auto_title.trim()) return r.auto_title.trim();
  if (r.question && r.question.trim()) return r.question.trim();
  const words = ((r.display_text || r.transcript) || "").split(/\s+/).filter(Boolean).slice(0, 8);
  return words.join(" ") + (words.length === 8 ? "…" : "");
}

// R3 — friendly, never guilt-tripping phrasing for how long ago the last memo was.
function silenceRelativeNl(iso) {
  if (!iso) return null;
  try {
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (days < 21) return `${days} dagen geleden`;
    const weeks = Math.floor(days / 7);
    if (weeks < 9) return `${weeks} weken geleden`;
    const months = Math.floor(days / 30);
    return `${months} ${months === 1 ? "maand" : "maanden"} geleden`;
  } catch {
    return null;
  }
}

export default function RecordPage() {
  const { user } = useAuth();
  const game = useGame(user);
  const { setHasUnsaved } = useUnsaved();
  const { categories: categoryList, names: categoryNames, loading: categoriesLoading, reload: reloadCategories } = useCategories();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---------- Mode selection ----------
  // Server-side Whisper is the DEFAULT capture mode on every device.
  //
  // Rationale: iOS Safari exposes webkitSpeechRecognition but its recognizer captures only
  // fragments in the field (35s audio → 16 words with duplicates). Web Speech is unusable
  // outside of desktop Chrome/Edge, so we no longer route users there by default.
  //
  // URL overrides for debugging:
  //   ?stt=server  → force server-side Whisper (default anyway)
  //   ?stt=live    → force Web Speech ("Live meeschrijven"), only useful on desktop Chrome/Edge
  const sttParam = searchParams.get("stt");
  const forceServer = sttParam === "server";
  const forceLive = sttParam === "live";

  const webSpeechSupported = isSpeechRecognitionSupported();
  const mediaRecSupported = isMediaRecorderSupported();

  // Detect environments where Web Speech is known-broken. iOS Safari / iOS webviews /
  // most in-app browsers fall in this bucket.
  const liveModeAvailable =
    webSpeechSupported && !isBrokenWebSpeechEnv() && !isIOSLike();

  // Live-meeschrijven opt-in (persisted per device). NEVER on iOS/webviews.
  const [liveOptIn, setLiveOptIn] = useState(() => {
    try { return localStorage.getItem("tw1_prefer_live") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("tw1_prefer_live", liveOptIn ? "1" : "0"); } catch { /* noop */ }
  }, [liveOptIn]);

  // Final mode decision.
  const useServer = forceServer
    ? true
    : forceLive
      ? !liveModeAvailable // if live is unavailable, fall back to server even when forced
      : !(liveModeAvailable && liveOptIn);

  const speech = useSpeechRecorder({ lang: "nl-NL" });
  const audio = useAudioRecorder();

  // ---------- Shared state ----------
  const [questions, setQuestions] = useState([]);
  const [visibleQuestions, setVisibleQuestions] = useState([]);
  // Round H — welke vragen deze sessie al langs zijn geweest (pas leeg als de lijst rond is).
  const shownQuestionIdsRef = useRef([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [freeMode, setFreeMode] = useState(false);

  const [editedTranscript, setEditedTranscript] = useState("");
  // D3 — Whisper silence-hallucination guard: set on the review screen when the server-side
  // detector flagged this transcript (even after its one retry). transcriptCollapsed (internal,
  // never shown to the user) is sent to /categorize instead of the raw looping text.
  const [transcriptWarning, setTranscriptWarning] = useState(false);
  const [transcriptCollapsed, setTranscriptCollapsed] = useState("");
  const [category, setCategory] = useState("");
  const [categoryIsSuggested, setCategoryIsSuggested] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [transform, setTransform] = useState("raw");
  const [showTransformInfo, setShowTransformInfo] = useState(false);
  const [parentId, setParentId] = useState(null);
  const [followUpCard, setFollowUpCard] = useState(null); // { parentId, question, category }
  const [savedMemo, setSavedMemo] = useState(null); // Round J — { id, scope, user_location } of the last save
  const followUpPollRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [processingLabel, setProcessingLabel] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | recording | uploading | review
  const [uploadError, setUploadError] = useState("");
  const [longWait, setLongWait] = useState(false);
  const pollRef = useRef(null);
  const warnedRef = useRef(false);
  const categoryRef = useRef("");
  const categorizedRef = useRef(false);
  const categorizeTokenRef = useRef(0);

  const recording = useServer ? audio.recording : speech.recording;
  const duration = useServer ? audio.duration : speech.duration;
  const recError = useServer ? audio.error : speech.error;
  // C2 — pause/resume only exists on the server-Whisper path (MediaRecorder.pause/resume);
  // "Live meeschrijven" (Web Speech) has no pause control (see the hint near the toggle).
  const paused = useServer ? audio.paused : false;
  const pauseAvailable = useServer && audio.pauseSupported;

  // ---------- Effects ----------
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/questions");
        setQuestions(data);
        const first = nextQuestionSet(data, [], user?.title);
        shownQuestionIdsRef.current = first.shown;
        setVisibleQuestions(first.questions);
      } catch (e) {
        console.warn("Could not load questions", e);
      } finally {
        setQuestionsLoaded(true);
      }
    })();
  }, [user?.title]);

  // ---------- Round M: a question picked on /trefwoorden (?vraag=<questionId>) ----------
  // Applied once per id, then stripped from the URL so a reload/Back never re-picks it.
  const vraagId = searchParams.get("vraag");
  const vraagAppliedRef = useRef(null);
  useEffect(() => {
    if (!vraagId || vraagAppliedRef.current === vraagId || !questionsLoaded || categoriesLoading) return;
    vraagAppliedRef.current = vraagId;
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete("vraag"); return next; }, { replace: true });
    if (restoredOnceRef.current === false && user?.id && safeReadDraft(user.id)) {
      toast.error("Herstel of verwijder eerst je concept.");
      return;
    }
    if (phase !== "idle" || selectedQuestion || freeMode) return;
    const found = questions.find((q) => q.id === vraagId);
    if (found) handlePickQuestion(found);
    else toast.error("Deze vraag staat niet (meer) in jouw stapel.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vraagId, questionsLoaded, categoriesLoading]);

  // ---------- B1.3: "Opnieuw inspreken" from Mijn kluis (?redo=<memoId>) ----------
  const redoId = searchParams.get("redo");
  const [replacesId, setReplacesId] = useState(null);
  const [redoBanner, setRedoBanner] = useState(null); // { id, title }
  const redoAppliedRef = useRef(false);
  useEffect(() => {
    if (!redoId || redoAppliedRef.current || !questionsLoaded || categoriesLoading) return;
    redoAppliedRef.current = true;
    (async () => {
      try {
        const { data: rec } = await api.get(`/recordings/${redoId}`);
        // FIX#13 (B-1) — admins can fetch ANY memo by id, but a redo/replace only makes
        // sense for your OWN memo (the server ignores a foreign replaces_id anyway).
        if (user?.id && rec.user_id && rec.user_id !== user.id) {
          toast.error("Je kunt alleen je eigen memo's opnieuw inspreken.");
          return;
        }
        const matched = rec.question ? (questions.find((q) => q.id === rec.question_id || q.text === rec.question)
          || (rec.question_id ? { id: rec.question_id, text: rec.question, category: rec.category } : null)) : null;
        if (matched) {
          // FIX#5 (B-1) — never carry a stale/inactive category on the reused question.
          setSelectedQuestion(categoryNames.includes(matched.category) ? matched : { ...matched, category: null });
          setFreeMode(false);
        } else {
          setSelectedQuestion(null);
          setFreeMode(true);
        }
        // Prefer the memo's own (still-active) category, then the matched question's;
        // otherwise clear it and let the AI-categorize effect run fresh (FIX#5).
        if (rec.category && categoryNames.includes(rec.category)) {
          setCategory(rec.category);
          setCategoryIsSuggested(false);
          categorizedRef.current = true;
        } else if (matched?.category && categoryNames.includes(matched.category)) {
          setCategory(matched.category);
          setCategoryIsSuggested(false);
          categorizedRef.current = true;
        } else {
          setCategory("");
          setCategoryIsSuggested(false);
          categorizedRef.current = false;
          categorizeTokenRef.current += 1;
        }
        // FIX#2 (B-1) — inherit the old memo's parent link so a redo of a thread's middle
        // node doesn't sever it (children are re-pointed to the new memo server-side too).
        setParentId(rec.parent_id || null);
        setReplacesId(rec.id);
        setRedoBanner({ id: rec.id, title: deriveTitleFallback(rec) });
      } catch {
        toast.error("Kon de memo niet laden om opnieuw in te spreken.");
      }
    })();
  }, [redoId, questionsLoaded, categoriesLoading, questions, categoryNames, user?.id]);

  // R3 — personal silence signal for the nudge banner (fail-soft, session-dismissible).
  const [activity, setActivity] = useState(null);
  const nudgeDismissKey = user?.id ? `tw1_nudge_dismissed_${user.id}` : null;
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  useEffect(() => {
    // Restore per-session dismissal so it stays gone across SPA navigation.
    if (!nudgeDismissKey) return;
    try {
      setNudgeDismissed(sessionStorage.getItem(nudgeDismissKey) === "1");
    } catch {
      setNudgeDismissed(false);
    }
  }, [nudgeDismissKey]);
  const dismissNudge = () => {
    setNudgeDismissed(true);
    try { if (nudgeDismissKey) sessionStorage.setItem(nudgeDismissKey, "1"); } catch { /* ignore */ }
  };
  // Round U — "Later" on the welcome card is remembered for today, per user.
  const starterLaterKey = user?.id ? `tw1_starter_later_${user.id}` : null;
  const [starterLater, setStarterLater] = useState(false);
  useEffect(() => {
    try {
      setStarterLater(!!starterLaterKey && localStorage.getItem(starterLaterKey) === todayKey());
    } catch {
      setStarterLater(false);
    }
  }, [starterLaterKey]);
  const dismissStarter = () => {
    setStarterLater(true);
    try { if (starterLaterKey) localStorage.setItem(starterLaterKey, todayKey()); } catch { /* ignore */ }
  };
  // Round U fix — after a save the welcome card stays away for this session (state, not localStorage).
  const [starterHiddenAfterSave, setStarterHiddenAfterSave] = useState(false);
  const loadActivity = useCallback(async () => {
    try {
      const { data } = await api.get("/me/activity");
      setActivity(data);
    } catch {
      setActivity(null); // fail-soft: no nudge
    }
  }, []);
  useEffect(() => {
    if (!user?.id) return;
    loadActivity();
  }, [user?.id, loadActivity]);

  useEffect(() => {
    const unsaved =
      phase === "recording" ||
      phase === "uploading" ||
      (phase === "review" && editedTranscript.trim().length > 0);
    setHasUnsaved(unsaved);
  }, [phase, editedTranscript, setHasUnsaved]);
  useEffect(() => () => setHasUnsaved(false), [setHasUnsaved]);

  // ---------- Concept-herstel (draft auto-save) ----------
  const [draftBanner, setDraftBanner] = useState(null);
  const restoredOnceRef = useRef(false);

  // On mount / user change: offer a fresh draft if we have one for THIS user.
  useEffect(() => {
    if (!user?.id) return;
    if (restoredOnceRef.current) return; // don't re-offer after user acted or restored
    const d = safeReadDraft(user.id);
    if (d) setDraftBanner(d);
  }, [user?.id]);

  // Latest-state ref keeps the 3s interval reading fresh values without stale closures.
  const draftStateRef = useRef({});
  draftStateRef.current = {
    phase,
    editedTranscript,
    speechTranscript: speech.transcript,
    speechInterim: speech.interim,
    selectedQuestion,
    category,
    transform,
    useServer,
  };

  const writeSnapshot = useCallback(() => {
    if (!user?.id) return;
    const s = draftStateRef.current;
    if (s.phase === "idle") return; // nothing to snapshot yet
    // Compute the "current" transcript for this phase.
    let live = "";
    if (s.phase === "review") {
      live = s.editedTranscript || "";
    } else if (!s.useServer) {
      // Web Speech active: capture the running transcript + interim so a mid-record crash still leaves the last words.
      live = ((s.speechTranscript || "") + (s.speechInterim ? " " + s.speechInterim : "")).trim();
    } else {
      // Server (Whisper) mode: no live transcript is available until upload returns. We still save phase + selection.
      live = s.editedTranscript || "";
    }
    if (!live.trim()) return; // don't overwrite a good draft with an empty one
    safeWriteDraft(user.id, {
      transcript: live,
      selectedQuestion: s.selectedQuestion
        ? { id: s.selectedQuestion.id, text: s.selectedQuestion.text, category: s.selectedQuestion.category ?? null }
        : null,
      category: s.category || "",
      transform: s.transform || "raw",
      phase: s.phase,
      savedAt: new Date().toISOString(),
    });
  }, [user?.id]);

  // Save on every phase change AND every ~3s while a memo is in progress.
  useEffect(() => {
    writeSnapshot();
  }, [phase, writeSnapshot]);

  useEffect(() => {
    if (phase === "idle") return;
    const id = setInterval(writeSnapshot, DRAFT_SAVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [phase, writeSnapshot]);

  // Keep a ref of the latest chosen category so the async suggestion never
  // overwrites a value the user picked while the AI call was in flight.
  useEffect(() => { categoryRef.current = category; }, [category]);

  // R2 — AI suggests a category in the review phase, fail-soft. Skips entirely
  // when the memo came from a kennisvraag that already carries a category, or
  // when a category is already set. Runs at most once per review session.
  useEffect(() => {
    if (phase !== "review") return;
    const t = editedTranscript.trim();
    if (!t) return;
    if (categoryRef.current) return;            // already chosen (user or kennisvraag)
    if (selectedQuestion?.category) return;     // kennisvraag category path — no AI
    if (categorizedRef.current) return;         // only once per review session
    categorizedRef.current = true;
    const myToken = categorizeTokenRef.current; // bound to THIS review session
    setCategorizing(true);
    (async () => {
      try {
        // D3 — send the collapsed (repetition-folded) text when this transcript was
        // flagged, so a Whisper hallucination loop doesn't waste the categorize AI call.
        const categorizeInput = transcriptWarning && transcriptCollapsed ? transcriptCollapsed : t;
        const { data } = await api.post("/categorize", { transcript: categorizeInput });
        // Ignore a late response from a review session that was reset/switched meanwhile.
        if (myToken !== categorizeTokenRef.current) return;
        const sug = data?.suggested_category;
        if (sug && categoryNames.includes(sug) && !categoryRef.current) {
          setCategory(sug);
          setCategoryIsSuggested(true);
        }
      } catch {
        // fail-soft: leave the Select empty exactly like before
      } finally {
        if (myToken === categorizeTokenRef.current) setCategorizing(false);
      }
    })();
  }, [phase, editedTranscript, selectedQuestion, transcriptWarning, transcriptCollapsed]);

  const restoreDraft = useCallback(() => {
    if (!draftBanner) return;
    restoredOnceRef.current = true;
    setEditedTranscript(draftBanner.transcript || "");
    if (draftBanner.category && categoryNames.includes(draftBanner.category)) {
      setCategory(draftBanner.category);
      setCategoryIsSuggested(false);
    } else if (draftBanner.category) {
      // Category was renamed/deactivated since the draft was saved — clear the picker and
      // let the AI-categorize effect run fresh instead of showing an empty, stuck Select.
      setCategory("");
      setCategoryIsSuggested(false);
      categorizedRef.current = false;
      categorizeTokenRef.current += 1;
      toast.info("De categorie van dit concept bestaat niet meer — kies een nieuwe.");
    }
    if (draftBanner.transform) setTransform(draftBanner.transform);
    // Restore question selection if it still exists in the loaded question set;
    // otherwise fall back to free-mode so the review flow works either way.
    const q = draftBanner.selectedQuestion;
    if (q && q.id) {
      const found = questions.find((x) => x.id === q.id);
      if (found) {
        // FIX#5 (B-1) — never carry a stale/inactive category on the restored question.
        setSelectedQuestion(categoryNames.includes(found.category) ? found : { ...found, category: null });
        setFreeMode(false);
      } else {
        // Question was removed/edited since — keep the text as free-mode context.
        setSelectedQuestion(null);
        setFreeMode(true);
      }
    } else {
      setSelectedQuestion(null);
      setFreeMode(true);
    }
    setPhase("review");
    setDraftBanner(null);
    toast.success("Concept hersteld.");
  }, [draftBanner, questions, categoryNames]);

  const discardDraft = useCallback(() => {
    if (!user?.id) return;
    restoredOnceRef.current = true;
    safeClearDraft(user.id);
    setDraftBanner(null);
    toast("Concept verwijderd.");
  }, [user?.id]);


  const shuffle = useCallback(() => {
    if (questions.length === 0) return;
    const next = nextQuestionSet(questions, shownQuestionIdsRef.current, user?.title);
    shownQuestionIdsRef.current = next.shown;
    setVisibleQuestions(next.questions);
  }, [questions, user?.title]);

  const handlePickQuestion = (q) => {
    // New doorvraag-context: never carry a stale parent link or suppress the fresh AI suggestion.
    setParentId(null);
    categorizedRef.current = false;
    categorizeTokenRef.current += 1;
    setFreeMode(false);
    if (q?.category && categoryNames.includes(q.category)) {
      setSelectedQuestion(q);
      setCategory(q.category);
      setCategoryIsSuggested(false);
    } else {
      // Category missing/deactivated since the question was written — clear the picker AND
      // strip it from selectedQuestion so the AI-categorize effect still runs (same pattern
      // as answerFollowUp below), instead of leaving an empty picker with no AI suggestion.
      setSelectedQuestion(q ? { ...q, category: null } : q);
      setCategory("");
      setCategoryIsSuggested(false);
    }
  };

  const handleFreeMode = () => {
    setParentId(null);
    categorizedRef.current = false;
    categorizeTokenRef.current += 1;
    setSelectedQuestion(null);
    setFreeMode(true);
  };

  // Web Speech path
  const startWebSpeech = () => {
    setPhase("recording");
    setEditedTranscript("");
    setTranscriptWarning(false);
    setTranscriptCollapsed("");
    speech.start();
  };
  const stopWebSpeech = () => {
    speech.stop();
    setEditedTranscript(speech.transcript.trim());
    setPhase("review");
  };

  // Whisper server path — async job pattern: POST returns a job id, then poll status.
  // This survives ingress/proxy timeouts and flaky mobile networks on long recordings.
  const uploadBlob = useCallback(async (blob, mimeMeta) => {
    setPhase("uploading");
    setUploadError("");
    const ext = mimeMeta?.ext || "webm";
    const filename = `memo-${Date.now()}.${ext}`;
    setLongWait(!!(blob && blob.size > LONG_AUDIO_BYTES));
    const form = new FormData();
    form.append("file", blob, filename);
    const token = localStorage.getItem("tw1_token");
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      // 1) Start the job. Only the upload itself needs a generous timeout (up to 80 MB).
      const startCtl = new AbortController();
      const startTid = setTimeout(() => startCtl.abort(), 120_000);
      let startResp;
      try {
        startResp = await fetch(`${API}/transcribe`, {
          method: "POST",
          headers: authHeaders,
          body: form,
          signal: startCtl.signal,
        });
      } finally {
        clearTimeout(startTid);
      }
      if (!startResp.ok) {
        const body = await startResp.json().catch(() => ({}));
        const detail = body?.detail;
        if (detail) throw new Error(detail);
        if (startResp.status === 413) throw new Error("Audio te groot — maximaal 80 MB. Neem korter op.");
        if (startResp.status === 401 || startResp.status === 403) throw new Error("Niet ingelogd of geen toegang.");
        if (startResp.status === 429) throw new Error("Te veel verzoeken — probeer het over een minuut opnieuw.");
        throw new Error("Audio-upload mislukt.");
      }
      const startData = await startResp.json();
      const jobId = startData?.job_id;
      if (!jobId) throw new Error("Audio-upload mislukt.");

      // 2) Poll status until done/error. Polling is NOT rate-limited server-side.
      // 32-min sanity ceiling: worst-case legitimate worker budget is ~28 min for a 60-min
      // memo. The server's "processing" status remains the real driver; this only stops a
      // truly wedged client. (Server-side dead-job guard flips a stalled job to error too.)
      const deadline = Date.now() + 32 * 60 * 1000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => { pollRef.current = setTimeout(r, 2500); });
        if (Date.now() > deadline) throw new Error("Duurde te lang — probeer opnieuw.");
        let sResp;
        try {
          sResp = await fetch(`${API}/transcribe/${jobId}`, { headers: authHeaders });
        } catch (_net) {
          continue; // transient network blip on mobile — keep polling
        }
        if (!sResp.ok) {
          if (sResp.status === 404) throw new Error("Transcriptie-taak niet gevonden — probeer opnieuw.");
          continue; // transient — keep polling
        }
        const s = await sResp.json();
        if (s.status === "done") {
          const t = (s.transcript || "").trim();
          if (!t) {
            setUploadError("Geen tekst herkend — spreek iets luider of dichter bij de microfoon.");
          }
          setEditedTranscript(t);
          setTranscriptWarning(!!s.transcript_warning);
          setTranscriptCollapsed(s.transcript_collapsed || "");
          setPhase("review");
          return;
        }
        if (s.status === "error") {
          throw new Error(s.detail || "Audio-upload mislukt.");
        }
        // still processing → loop
      }
    } catch (e) {
      const msg = e?.name === "AbortError"
        ? "Duurde te lang — probeer opnieuw."
        : String(e?.message || e || "Audio-upload mislukt.");
      setUploadError(msg);
      setPhase("review");
    } finally {
      setLongWait(false);
      if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    }
  }, []);

  const startServer = async () => {
    setPhase("recording");
    setEditedTranscript("");
    setTranscriptWarning(false);
    setTranscriptCollapsed("");
    setUploadError("");
    warnedRef.current = false;
    await audio.start();
  };
  const stopServer = () => audio.stop();

  // Max recording length (server path): warn at 55:00, hard-stop at 60:00.
  useEffect(() => {
    if (phase !== "recording" || !useServer) return;
    if (duration >= WARN_RECORD_SEC && duration < MAX_RECORD_SEC && !warnedRef.current) {
      warnedRef.current = true;
      toast("Nog 5 minuten tot de maximale opnameduur (60 min).");
    }
    if (duration >= MAX_RECORD_SEC) {
      audio.stop();
      toast("Maximale opnameduur bereikt (60 min) — de opname staat klaar om te transcriberen.");
    }
  }, [duration, phase, useServer, audio]);

  // One-time debug log so we can confirm the MediaRecorder chose a Whisper-friendly mimeType on iOS.
  const loggedMimeRef = useRef(false);
  useEffect(() => {
    if (audio.mimeMeta && !loggedMimeRef.current) {
      loggedMimeRef.current = true;
      console.info(`[TW1 Audio] MediaRecorder mimeType = ${audio.mimeMeta.mime} (.${audio.mimeMeta.ext})`);
    }
  }, [audio.mimeMeta]);

  useEffect(() => {
    if (useServer && audio.blob && phase === "recording") {
      uploadBlob(audio.blob, audio.mimeMeta);
    }
  }, [audio.blob, audio.mimeMeta, phase, useServer, uploadBlob]);

  const retryUpload = () => {
    if (audio.blob) uploadBlob(audio.blob, audio.mimeMeta);
  };

  // Unified controls
  const canRecord =
    (useServer ? mediaRecSupported : webSpeechSupported) &&
    (selectedQuestion !== null || freeMode);
  const hasChosen = selectedQuestion !== null || freeMode;

  const handleStart = () => {
    if (useServer) startServer();
    else startWebSpeech();
  };
  const handleStop = () => {
    if (useServer) stopServer();
    else stopWebSpeech();
  };
  // C2 — pause/resume the SAME take (server-Whisper path only).
  const handlePause = () => audio.pause();
  const handleResume = () => audio.resume();

  // Full reset — used after a SUCCESSFUL save to return to a clean idle picker with a
  // fresh set of question cards (question/category/parent intentionally NOT kept here).
  const resetAllState = useCallback(() => {
    if (useServer) audio.reset();
    else speech.reset();
    setEditedTranscript("");
    setTranscriptWarning(false);
    setTranscriptCollapsed("");
    setCategory("");
    setCategoryIsSuggested(false);
    categorizedRef.current = false;
    categorizeTokenRef.current += 1;   // invalidate any in-flight categorize from this session
    setParentId(null);
    setPhase("idle");
    setSelectedQuestion(null);
    setFreeMode(false);
    setUploadError("");
    if (questions.length > 0) {
      const fresh = nextQuestionSet(questions, shownQuestionIdsRef.current, user?.title);
      shownQuestionIdsRef.current = fresh.shown;
      setVisibleQuestions(fresh.questions);
    }
    if (user?.id) safeClearDraft(user.id);
    setReplacesId(null);
    setRedoBanner(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useServer, audio, speech, questions, user?.id, user?.title]);

  // Shared rule (mirrors handlePickQuestion ~516-527): adopt a question's category only
  // while it's still active; otherwise null it out and reset the categorize token so the
  // AI-suggest effect runs fresh instead of leaving an empty, stuck picker (FIX#4/#5, B-1).
  const applyQuestionCategory = useCallback((q) => {
    if (q?.category && categoryNames.includes(q.category)) {
      setSelectedQuestion(q);
      setCategory(q.category);
      setCategoryIsSuggested(false);
      categorizedRef.current = true;
    } else {
      setSelectedQuestion(q ? { ...q, category: null } : q);
      setCategory("");
      setCategoryIsSuggested(false);
      categorizedRef.current = false;
      categorizeTokenRef.current += 1;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryNames]);

  // B1.2 — "Opname verwijderen": discards THIS take (audio/transcript/category/draft) but
  // KEEPS the selected kennisvraag (and any parent/redo context) — the vraag blijft staan.
  // Changing the question afterwards still works via the cards, shuffle, or the X chip.
  const handleReset = () => {
    if (useServer) audio.reset();
    else speech.reset();
    setEditedTranscript("");
    setTranscriptWarning(false);
    setTranscriptCollapsed("");
    setUploadError("");
    setPhase("idle");
    if (user?.id) safeClearDraft(user.id);
    // FIX#4 (B-1) — re-derive the category from the KEPT question instead of always
    // blanking it, so the second take doesn't lose its category / AI suggestion.
    if (selectedQuestion) {
      applyQuestionCategory(selectedQuestion);
    } else {
      setCategory("");
      setCategoryIsSuggested(false);
      categorizedRef.current = false;
      categorizeTokenRef.current += 1;
    }
  };

  const captureCardRef = useRef(null);

  // B1.1 — "Opnieuw inspreken": discard this take but KEEP the question/parent context, and
  // keep the category ONLY if the user picked it manually (an AI suggestion is discarded).
  const handleReRecord = () => {
    const hasText = editedTranscript.trim().length > 0;
    const doIt = () => {
      if (useServer) audio.reset();
      else speech.reset();
      setEditedTranscript("");
      setTranscriptWarning(false);
      setTranscriptCollapsed("");
      setUploadError("");
      categorizedRef.current = false;
      categorizeTokenRef.current += 1;
      if (categoryIsSuggested) {
        // FIX#4 (B-1) — same re-derive rule as handleReset, instead of just blanking it.
        if (selectedQuestion) applyQuestionCategory(selectedQuestion);
        else {
          setCategory("");
          setCategoryIsSuggested(false);
        }
      }
      setPhase("idle");
      if (user?.id) safeClearDraft(user.id);
      requestAnimationFrame(() => {
        captureCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    };
    if (hasText && !window.confirm("Opname weggooien en opnieuw inspreken? De vraag blijft staan.")) return;
    doIt();
  };

  // R4 — poll the just-saved memo for its AI follow-up question (~12s), then show the card.
  const pollFollowUp = useCallback((recId, parentCategory) => {
    if (followUpPollRef.current) { clearTimeout(followUpPollRef.current); }
    const deadline = Date.now() + 12000;
    const tick = async () => {
      if (Date.now() > deadline) return; // give up silently
      try {
        const { data } = await api.get(`/recordings/${recId}`);
        if (data?.follow_up_question) {
          setFollowUpCard({ parentId: recId, question: data.follow_up_question, category: parentCategory });
          return;
        }
      } catch {
        // fail-soft: no card
      }
      followUpPollRef.current = setTimeout(tick, 2500);
    };
    followUpPollRef.current = setTimeout(tick, 2500);
  }, []);

  useEffect(() => () => { if (followUpPollRef.current) clearTimeout(followUpPollRef.current); }, []);

  // R4 — tap the follow-up card: straight into the record flow, question + parent category prefilled.
  // Round R — "Goede vraag" keeps the card (marked), "Vage vraag" closes it with a thank-you.
  const sendFollowUpVerdict = async (verdict) => {
    if (!followUpCard) return;
    const card = followUpCard;
    try {
      await api.post("/followups/feedback", {
        parent_recording_id: card.parentId, followup_question: card.question, verdict,
      });
      if (verdict === "vague") {
        setFollowUpCard(null);
        toast.success("Bedankt — we vragen voortaan scherper door.");
      } else {
        setFollowUpCard((c) => (c && c.parentId === card.parentId ? { ...c, verdict: "good" } : c));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const answerFollowUp = () => {
    if (!followUpCard) return;
    setSelectedQuestion({
      id: `followup-${followUpCard.parentId}`,
      text: followUpCard.question,
      category: categoryNames.includes(followUpCard.category) ? followUpCard.category : null,
      functies: [],
    });
    setFreeMode(false);
    if (categoryNames.includes(followUpCard.category)) {
      setCategory(followUpCard.category);
      setCategoryIsSuggested(false);
    }
    categorizedRef.current = true; // parent category prefilled — no AI suggestion needed
    setParentId(followUpCard.parentId);
    setFollowUpCard(null);
  };

  const handleSubmit = async () => {
    const text = editedTranscript.trim();
    if (!text) {
      toast.error("Transcript is leeg. Neem eerst iets op.");
      return;
    }
    if (!category) {
      toast.error("Kies een categorie.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: rec } = await api.post("/recordings", {
        transcript: text,
        category,
        duration_seconds: duration,
        question: selectedQuestion ? selectedQuestion.text : null,
        question_id: selectedQuestion?.id || null,
        parent_id: parentId || null,
        replaces_id: replacesId || null,
        transcript_warning: transcriptWarning,
        transcript_collapsed: transcriptCollapsed || null,
      });
      // Fail-soft AI transform. The memo is already saved above.
      if (transform && transform !== "raw") {
        setProcessingLabel(TRANSFORM_LABELS[transform] || "Verwerken…");
        try {
          await api.post(`/recordings/${rec.id}/process`, { transform_type: transform });
          toast.success("Opgeslagen én verwerkt in je kluis.");
        } catch (_e) {
          toast.error("Verwerken mislukt — je transcript is wel opgeslagen.");
        } finally {
          setProcessingLabel("");
        }
      } else {
        toast.success("Opgeslagen in je kluis.");
      }
      if (replacesId && rec.replaced_ok === false) {
        toast.error("Nieuwe memo bewaard, de oude kon niet worden verwijderd.");
      }
      // Successful submit — clear the draft; nothing left to recover.
      if (user?.id) safeClearDraft(user.id);
      const savedCategory = category;
      const savedId = rec.id;
      const savedQuestion = selectedQuestion;
      resetAllState();
      setNudgeDismissed(true);
      setStarterHiddenAfterSave(true);
      setQuestions((qs) => markQuestionAnswered(qs, savedQuestion));
      loadActivity();
      setSavedMemo({ id: rec.id, scope: rec.scope, user_location: rec.user_location });
      game.afterSave(savedId);
      // R4 — briefly poll for the AI follow-up question, then offer it (fail-soft).
      pollFollowUp(savedId, savedCategory);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const liveTranscriptDisplay = useServer
    ? ""
    : (speech.transcript + (speech.interim ? " " + speech.interim : "")).trim();

  const featured = visibleQuestions[0] || null;
  const others = visibleQuestions.slice(1, 3);
  const openQuestionsCount = questions.filter((q) => !q.has_memo && isForMe(q, user?.title)).length;

  // ---------- Render: two big screens (Opnemen vs Opname verwerken) ----------
  const isCaptureScreen = phase !== "idle" || hasChosen;

  // R3 — personal stilte-nudge. Shows only on the idle screen when the user is
  // silent (no memos or > 14 days). Round U: same pick rule as the welcome card (never another functie).
  const nudgeQuestion = pickStarterQuestion(questions, user?.title);
  // Round U — welcome card: first-time user, or "welkom terug" when the last memo is 14+ days old.
  const starterKind = starterMode({ isStarter: game.isStarter, totalMemos: game.streak?.total_memos || 0,
    lastMemoAt: activity?.last_memo_at, role: user?.role });
  // Round U fix — decide between the welcome card and the old banner only once the streak is known (no flash).
  const starterReady = game.streak !== null;
  const showStarter = starterReady && !!starterKind && !!nudgeQuestion && !draftBanner && !starterLater && !starterHiddenAfterSave;
  const showNudge =
    !isCaptureScreen && !draftBanner && !followUpCard && !nudgeDismissed && !game.isStarter && !showStarter
    && starterReady && (!starterKind || !nudgeQuestion) && !!activity?.is_silent;
  const nudgeAgo = silenceRelativeNl(activity?.last_memo_at);

  return (
    <div
      className="max-w-2xl mx-auto px-5 sm:px-8 py-6 sm:py-14"
      data-testid="record-page"
      data-mode={useServer ? "server" : "webspeech"}
      data-phase={phase}
    >
      {/* ==================================================================
          Screen: Opnemen (idle, question picker + free record)
          ================================================================== */}
      {!isCaptureScreen && (
        <div className="tw-fadeup" data-testid="opnemen-screen">
          <ScreenHeader
            kicker={`${greetingNl()}, ${user?.name || ""}`}
            title={
              <>
                Leg vast wat <span className="text-tw-yellow">ertoe doet.</span>
              </>
            }
            lead="Van losse gedachte naar bruikbare teamkennis — spreek vrijuit of start met een gerichte vraag."
          />

          {/* R4 — slimme doorvraag na opslaan (verschijnt zodra de AI klaar is; altijd wegklikbaar) */}
          {savedMemo && (
            <SavedScopeCard
              memo={savedMemo}
              onChange={(patch) => setSavedMemo((m) => (m ? { ...m, ...patch } : m))}
              onClose={() => setSavedMemo(null)}
            />
          )}
          <SaveReward reward={game.reward} />
          <StreakChip streak={game.streak} />
          <SeasonKickoff data={game.season} user={user} />
          {game.showStanding && <WeekStanding week={game.week} compact prefix="record-home-week" />}
          {showStarter && <StarterCard question={nudgeQuestion} mode={starterKind} user={user} onPick={handlePickQuestion} onLater={dismissStarter} />}
          <ChallengeNotices data={game.challenges} user={user} reload={game.reload} />
          <KeywordNotices user={user} prefix="record-keyword-notices" />
          <SiteGapsLine user={user} questions={questions} onPick={handlePickQuestion} refreshKey={`${savedMemo?.id}|${savedMemo?.scope}|${savedMemo?.user_location}`} />
          {followUpCard && (
            <div
              className="bg-tw-surface border-l-4 border-tw-yellow border-y border-r border-tw p-4 sm:p-5 mb-6 tw-fadeup"
              data-testid="followup-card"
              role="status"
            >
              <div className="flex items-start gap-3">
                <Sparkles size={16} className="text-tw-yellow shrink-0 mt-0.5" strokeWidth={2.5} />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow uppercase mb-1">
                    Opgeslagen · slimme doorvraag
                  </div>
                  <p className="text-[15px] text-white leading-snug font-display font-bold" data-testid="followup-question">
                    &ldquo;{followUpCard.question}&rdquo;
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                      onClick={answerFollowUp}
                      data-testid="followup-answer-btn"
                      className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold px-4"
                    >
                      <Mic size={12} className="mr-1.5" strokeWidth={3} />
                      Beantwoord deze vraag
                    </Button>
                    <button
                      type="button"
                      onClick={() => sendFollowUpVerdict("good")}
                      disabled={followUpCard.verdict === "good"}
                      aria-pressed={followUpCard.verdict === "good"}
                      data-testid="followup-good-btn"
                      className={`tw-press inline-flex items-center gap-1 h-9 px-3 border font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${followUpCard.verdict === "good" ? "border-[#32D74B] text-[#32D74B]" : "border-tw text-tw-muted hover:text-white hover:border-white"}`}
                    >
                      {followUpCard.verdict === "good" ? <Check size={12} strokeWidth={3} /> : <ThumbsUp size={12} strokeWidth={2.5} />}
                      Goede vraag
                    </button>
                    <button
                      type="button"
                      onClick={() => sendFollowUpVerdict("vague")}
                      data-testid="followup-vague-btn"
                      className="tw-press inline-flex items-center gap-1 h-9 px-3 border border-tw text-tw-muted hover:text-white hover:border-white font-mono text-[10px] uppercase tracking-[0.15em] transition-colors"
                    >
                      <ThumbsDown size={12} strokeWidth={2.5} />
                      Vage vraag
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setFollowUpCard(null)}
                  data-testid="followup-dismiss"
                  aria-label="Later"
                  className="text-tw-muted hover:text-white transition-colors shrink-0 p-1 font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-1"
                >
                  Later <X size={14} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          )}

          {/* R3 — persoonlijke stilte-nudge (uitnodigend, dismissible, blokkeert niets) */}
          {showNudge && (
            <div
              className="bg-tw-yellow/5 border-l-4 border-tw-yellow border-y border-r border-tw p-4 sm:p-5 mb-6 tw-fadeup"
              data-testid="silence-nudge"
              role="status"
            >
              <div className="flex items-start gap-3">
                <Sparkles size={16} className="text-tw-yellow shrink-0 mt-0.5" strokeWidth={2.5} />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow uppercase mb-1">
                    Welkom terug
                  </div>
                  <p className="text-sm text-white leading-snug" data-testid="silence-nudge-text">
                    {nudgeAgo
                      ? <>Je laatste memo was <span className="text-tw-yellow">{nudgeAgo}</span>. </>
                      : <>Je hebt nog geen memo ingesproken. </>}
                    {nudgeQuestion
                      ? "Deze vraag staat voor je klaar:"
                      : "Spreek vrijuit iets in wat vandaag speelt."}
                  </p>
                  {nudgeQuestion && (
                    <p className="mt-2 text-[15px] text-white leading-snug font-display font-bold" data-testid="silence-nudge-question">
                      &ldquo;{nudgeQuestion.text}&rdquo;
                    </p>
                  )}
                  <div className="mt-3">
                    {nudgeQuestion ? (
                      <Button
                        onClick={() => handlePickQuestion(nudgeQuestion)}
                        data-testid="silence-nudge-answer-btn"
                        className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold px-4"
                      >
                        <Mic size={12} className="mr-1.5" strokeWidth={3} />
                        Beantwoord deze vraag
                      </Button>
                    ) : (
                      <Button
                        onClick={handleFreeMode}
                        data-testid="silence-nudge-free-btn"
                        className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold px-4"
                      >
                        <AudioLines size={12} className="mr-1.5" strokeWidth={3} />
                        Vrij inspreken
                      </Button>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={dismissNudge}
                  data-testid="silence-nudge-close"
                  aria-label="Sluit"
                  className="text-tw-muted hover:text-white transition-colors shrink-0 p-1"
                >
                  <X size={14} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          )}

          {/* Concept-herstel banner — only when a draft <24h exists for THIS user */}
          {draftBanner && (
            <div
              className="bg-tw-surface border-l-4 border-tw-yellow border-y border-r border-tw p-4 sm:p-5 mb-6 tw-fadeup"
              data-testid="draft-banner"
              role="alert"
            >
              <div className="flex items-start gap-3 mb-3">
                <History size={16} className="text-tw-yellow shrink-0 mt-0.5" strokeWidth={2.5} />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow uppercase mb-1">
                    Concept-herstel
                  </div>
                  <p className="text-sm text-white leading-snug">
                    Niet-opgeslagen concept gevonden{" "}
                    <span className="text-tw-muted font-mono text-xs" data-testid="draft-banner-age">
                      ({draftRelativeNl(draftBanner.savedAt)})
                    </span>
                  </p>
                  {draftBanner.transcript && (
                    <p
                      className="mt-2 text-xs text-tw-muted italic line-clamp-2 leading-relaxed"
                      data-testid="draft-banner-preview"
                    >
                      {`"${draftBanner.transcript.slice(0, 160)}${draftBanner.transcript.length > 160 ? "…" : ""}"`}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={discardDraft}
                  data-testid="draft-banner-close"
                  aria-label="Sluit"
                  className="text-tw-muted hover:text-white transition-colors shrink-0 p-1"
                >
                  <X size={14} strokeWidth={2.5} />
                </button>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={restoreDraft}
                  data-testid="draft-banner-restore"
                  className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold px-4"
                >
                  <History size={12} className="mr-1.5" strokeWidth={3} />
                  Herstel
                </Button>
                <Button
                  onClick={discardDraft}
                  variant="outline"
                  data-testid="draft-banner-discard"
                  className="h-9 rounded-none bg-transparent border-tw text-white hover:bg-tw-surface-hover hover:text-white uppercase tracking-[0.2em] text-[10px] font-bold"
                >
                  Verwijder
                </Button>
              </div>
            </div>
          )}

          {/* Server-mode note (kept quiet) */}
          {useServer && <ServerModeNote />}
          {useServer && !mediaRecSupported && <MediaRecUnsupportedBanner />}

          {/* Live-meeschrijven toggle — only on browsers where Web Speech actually works */}
          {liveModeAvailable && (
            <LiveModeToggle enabled={liveOptIn} onChange={setLiveOptIn} />
          )}

          {/* Featured "Vraag van vandaag" card */}
          {featured && (
            <article
              data-testid="today-question-card"
              className="bg-tw-surface border border-tw p-5 sm:p-6 mb-6"
            >
              <div className="flex items-center justify-between mb-4">
                <Kicker>Vraag van vandaag</Kicker>
                <div className="flex items-center gap-3">
                  {openQuestionsCount > 0 && (
                    <span
                      data-testid="open-questions-chip"
                      className="font-mono text-[10px] tracking-[0.15em] text-tw-muted uppercase"
                    >
                      {openQuestionsCount} open
                    </span>
                  )}
                  {questions.length > 3 && (
                    <button
                      onClick={shuffle}
                      data-testid="question-shuffle-btn"
                      aria-label="Andere vragen laden"
                      title="Andere vragen"
                      className="tw-press flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-tw-muted hover:text-tw-yellow uppercase px-1.5 py-1 focus:outline-none focus-visible:text-tw-yellow"
                    >
                      <Shuffle size={12} strokeWidth={2.5} /> Wissel
                    </button>
                  )}
                </div>
              </div>

              {featured.category && <CategoryChip name={featured.category} />}
              {featured.is_focus && <FocusTag id={featured.id} />}
              <FunctieLabel q={featured} title={user?.title} />

              {featured.answered && (
                <span
                  className="ml-2 inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] uppercase text-[#32D74B]"
                  data-testid={`question-answered-${featured.id}`}
                >
                  <Check size={11} strokeWidth={3} /> Beantwoord
                </span>
              )}

              <h2 className="mt-3 mb-5 font-display text-xl sm:text-2xl font-bold text-white leading-snug tracking-tight">
                {featured.text}
              </h2>

              <button
                type="button"
                onClick={() => handlePickQuestion(featured)}
                data-testid={`question-card-${featured.id}`}
                className="tw-press w-full h-12 bg-tw-yellow text-black hover:bg-[#FFDE33] font-bold uppercase tracking-[0.2em] text-xs flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#141414]"
              >
                <Mic size={14} strokeWidth={2.5} />
                Beantwoord deze vraag
              </button>
            </article>
          )}

          {/* Andere invalshoeken */}
          {others.length > 0 && (
            <section data-testid="other-questions" className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <Kicker>Andere invalshoeken</Kicker>
                <span className="font-mono text-[10px] tracking-[0.2em] text-tw-muted uppercase">
                  {others.length} {others.length === 1 ? "suggestie" : "suggesties"}
                </span>
              </div>
              <ul className="divide-y divide-[#262626]" data-testid="other-questions-list">
                {others.map((q) => (
                  <li key={q.id}>
                    <button
                      type="button"
                      onClick={() => handlePickQuestion(q)}
                      data-testid={`question-card-${q.id}`}
                      className="tw-press group w-full text-left flex items-center gap-3 py-4 px-1 hover:bg-tw-surface/60 focus:outline-none focus-visible:bg-tw-surface"
                    >
                      <div className="flex-1 min-w-0">
                        {q.category && <CategoryChip name={q.category} compact />}
                        {q.is_focus && <FocusTag id={q.id} />}
                        <FunctieLabel q={q} title={user?.title} />
                        {q.answered && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] uppercase text-[#32D74B] align-middle"
                            data-testid={`question-answered-${q.id}`}
                          >
                            <Check size={10} strokeWidth={3} /> Beantwoord
                          </span>
                        )}
                        <p className="mt-1.5 text-[15px] text-white leading-snug">
                          {q.text}
                        </p>
                      </div>
                      <span
                        aria-hidden
                        className="shrink-0 w-8 h-8 rounded-full border border-tw text-tw-muted group-hover:text-tw-yellow group-hover:border-tw-yellow flex items-center justify-center"
                      >
                        <ArrowUpRight size={14} strokeWidth={2.5} />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Round M — een stil linkje naar trefwoorden */}
          <p className="mb-6 -mt-2 text-xs text-tw-muted" data-testid="record-keywords-link-line">
            Mis je een onderwerp?{" "}
            <Link to="/trefwoorden" data-testid="record-keywords-link" className="text-white underline underline-offset-4 decoration-tw-yellow/60 hover:text-tw-yellow">
              Voeg een trefwoord toe
            </Link>
          </p>

          {/* Vrij inspreken block */}
          <div className="pt-6 border-t border-tw" data-testid="free-record-block">
            <button
              type="button"
              onClick={handleFreeMode}
              data-testid="record-free-btn"
              className="tw-press w-full h-12 flex items-center justify-center gap-2 border border-tw hover:border-tw-yellow text-white hover:text-tw-yellow uppercase tracking-[0.25em] text-[11px] font-bold focus:outline-none focus-visible:border-tw-yellow focus-visible:text-tw-yellow"
            >
              <AudioLines size={14} strokeWidth={2.5} />
              Vrij inspreken
            </button>
            <p className="mt-3 text-xs text-tw-muted">
              Je opname is privé totdat je deze indient.
            </p>
          </div>
          {game.showStanding && <div className="mt-8" data-testid="record-game-standings">
            {game.week?.standings?.length > 0 && <Link to="/weekstand" className="game-secondary" data-testid="record-game-open">Stand en uitdagingen <ArrowUpRight size={14} /></Link>}
          </div>}
        </div>
      )}

      {/* ==================================================================
          Screen: Opname verwerken (recording | uploading | review)
          ================================================================== */}
      {isCaptureScreen && (
        <div className="tw-fadeup" data-testid="capture-screen">
          {/* Screen header changes per phase */}
          <CaptureHeader
            phase={phase}
            duration={duration}
            recording={recording}
          />

          {/* Server-mode note stays quietly visible */}
          {game.showStanding && <WeekStanding week={game.week} compact prefix="record-game-compact" />}
          <ChallengeNotices data={game.challenges} user={user} reload={game.reload} prefix="record-game-challenges" />
          {useServer && phase !== "review" && <ServerModeNote compact />}

          {/* B1.3 — redo banner: "Opnieuw inspreken" was launched from Mijn kluis */}
          {redoBanner && (
            <div
              className="bg-tw-surface border-l-4 border-tw-yellow border-y border-r border-tw p-4 sm:p-5 mb-6 tw-fadeup"
              data-testid="redo-banner"
              role="status"
            >
              <div className="flex items-start gap-3">
                <RotateCcw size={16} className="text-tw-yellow shrink-0 mt-0.5" strokeWidth={2.5} />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow uppercase mb-1">
                    Opnieuw inspreken
                  </div>
                  <p className="text-sm text-white leading-snug">
                    Je vervangt de memo{" "}
                    <span className="text-tw-yellow" data-testid="redo-banner-title">&ldquo;{redoBanner.title}&rdquo;</span>{" "}
                    — na opslaan wordt de oude memo verwijderd.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setReplacesId(null); setRedoBanner(null); }}
                  data-testid="redo-banner-dismiss"
                  aria-label="Annuleer vervanging"
                  className="text-tw-muted hover:text-white transition-colors shrink-0 p-1"
                >
                  <X size={14} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          )}

          {/* Context banner — remains so the user knows what they're answering */}
          <SelectedContext
            selectedQuestion={selectedQuestion}
            freeMode={freeMode}
            phase={phase}
            onChange={() => {
              if (phase !== "idle") return;
              setParentId(null);
              categorizedRef.current = false;
              categorizeTokenRef.current += 1;
              setSelectedQuestion(null);
              setFreeMode(false);
            }}
          />

          {/* Recording status card (recording + uploading + review-summary orb) */}
          {phase !== "review" && (
            <div
              ref={captureCardRef}
              className="bg-tw-surface border border-tw p-6 sm:p-10 mb-6"
              data-testid="recording-status-card"
            >
              {phase === "recording" && (
                <div className="text-center mb-4">
                  <div
                    className={`font-display text-xl sm:text-2xl font-bold uppercase tracking-tight ${paused ? "text-tw-muted" : "text-tw-yellow"}`}
                    data-testid="record-status-label"
                  >
                    {paused ? "Gepauzeerd" : "Luisteren…"}
                  </div>
                </div>
              )}

              <Waveform
                active={recording && !paused}
                color={paused ? "#A3A3A3" : recording ? "#FF3B30" : "#FFD700"}
              />

              {/* Big orb — the centerpiece; idle breathes, recording pulses with rings, paused goes calm/static */}
              <div className="flex justify-center my-8 sm:my-10">
                {phase === "recording" || phase === "idle" ? (
                  <button
                    type="button"
                    onClick={recording ? handleStop : handleStart}
                    disabled={!canRecord}
                    data-testid="record-button"
                    aria-label={recording ? (paused ? "Opname stoppen (gepauzeerd)" : "Opname stoppen") : "Opname starten"}
                    className={`tw-press-lg tw-orb relative flex items-center justify-center w-44 h-44 sm:w-56 sm:h-56 rounded-full transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-tw-yellow focus:ring-offset-4 focus:ring-offset-[#141414] ${
                      paused
                        ? "bg-tw-red/60 text-white/85 tw-orb-paused"
                        : recording
                        ? "bg-tw-red text-white tw-orb-recording"
                        : canRecord
                        ? "bg-tw-yellow text-black tw-orb-idle hover:bg-[#FFDE33]"
                        : "bg-tw-yellow text-black tw-glow-yellow"
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    {recording && !paused && (
                      <span aria-hidden className="tw-orb-recording-body absolute inset-0 rounded-full bg-tw-red" />
                    )}
                    {recording ? (
                      <Square size={56} strokeWidth={2.5} fill="currentColor" className="relative z-10" />
                    ) : (
                      <Mic size={60} strokeWidth={2.5} className="relative z-10" />
                    )}
                    {paused && (
                      <span
                        aria-hidden
                        data-testid="record-paused-badge"
                        className="absolute top-1 right-1 sm:top-2 sm:right-2 w-8 h-8 rounded-full bg-tw-black border-2 border-tw-yellow flex items-center justify-center z-10"
                      >
                        <Pause size={14} className="text-tw-yellow" strokeWidth={3} fill="currentColor" />
                      </span>
                    )}
                  </button>
                ) : (
                  // phase === "uploading"
                  <div
                    className="flex flex-col items-center gap-4 py-10"
                    data-testid="uploading-state"
                  >
                    <Loader2 size={56} className="text-tw-yellow animate-spin" strokeWidth={2.5} />
                    <div className="font-display text-2xl font-black text-tw-yellow uppercase">
                      Transcriberen…
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted text-center px-4">
                      {longWait
                        ? "Dit kan bij lange opnames enkele minuten duren."
                        : "Whisper structureert je opname"}
                    </div>
                  </div>
                )}
              </div>

              {/* C2 — pause/resume control (server-Whisper path only, one blob/transcript for the whole take) */}
              {phase === "recording" && useServer && pauseAvailable && (
                <div className="flex justify-center mb-5 -mt-2">
                  <button
                    type="button"
                    onClick={paused ? handleResume : handlePause}
                    data-testid={paused ? "record-resume-btn" : "record-pause-btn"}
                    className="tw-press inline-flex items-center gap-2 h-10 px-4 border border-tw text-white hover:border-tw-yellow hover:text-tw-yellow uppercase tracking-[0.2em] text-[11px] font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-tw-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-[#141414]"
                  >
                    {paused ? (
                      <>
                        <Play size={13} strokeWidth={2.5} fill="currentColor" /> Hervat opname
                      </>
                    ) : (
                      <>
                        <Pause size={13} strokeWidth={2.5} fill="currentColor" /> Pauzeer opname
                      </>
                    )}
                  </button>
                </div>
              )}

              <div className="text-center font-mono text-[11px] tracking-[0.25em] text-tw-muted uppercase">
                {phase === "idle" && "Tik om te starten"}
                {phase === "recording" &&
                  (paused
                    ? "Tik hervatten om door te gaan · of stop en ga naar controle"
                    : useServer
                    ? "Tik om te stoppen · transcript verschijnt daarna"
                    : "Tik om te stoppen")}
                {phase === "uploading" && "Bezig met verwerken"}
              </div>

              {phase === "recording" && !useServer && (
                <p className="mt-3 text-center text-[11px] text-tw-muted" data-testid="pause-unavailable-live-hint">
                  Pauzeren is niet beschikbaar bij live meeschrijven.
                </p>
              )}

              {recError && (
                <div
                  className="mt-5 border border-tw-red bg-tw-red/10 text-tw-red text-xs px-3 py-2 tw-fadeup"
                  data-testid="speech-error"
                >
                  {recError}
                </div>
              )}
            </div>
          )}

          {/* Live transcript while recording (Web Speech) */}
          {phase === "recording" && (
            <div className="mt-2 tw-fadeup">
              {useServer ? (
                <div
                  className="bg-tw-surface/60 border border-tw p-5 min-h-[100px] text-tw-muted italic leading-relaxed"
                  data-testid="server-recording-placeholder"
                >
                  Transcript verschijnt na het stoppen.
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <Kicker>Transcript</Kicker>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-tw-red animate-pulse" />
                      <span className="font-mono text-[10px] tracking-[0.25em] text-tw-red">
                        LIVE
                      </span>
                    </div>
                  </div>
                  <div
                    className="bg-tw-surface border border-tw p-4 sm:p-5 min-h-[140px] text-white leading-relaxed"
                    data-testid="live-transcript"
                  >
                    {liveTranscriptDisplay ? (
                      <>
                        <span>{speech.transcript}</span>
                        {speech.interim && (
                          <span className="text-tw-muted italic"> {speech.interim}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-tw-muted italic">Aan het luisteren… begin gerust met praten.</span>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Upload error banner + retry */}
          {phase === "review" && uploadError && (
            <div
              className="mb-6 border border-tw-red bg-tw-red/10 p-4 sm:p-5 flex items-start gap-3 tw-fadeup"
              data-testid="upload-error-banner"
            >
              <AlertTriangle size={20} className="shrink-0 mt-0.5 text-tw-red" />
              <div className="flex-1 min-w-0">
                <div className="font-bold uppercase tracking-wider text-tw-red text-sm mb-1">
                  Transcriptie mislukt
                </div>
                <div className="text-sm text-tw-muted break-words">{uploadError}</div>
                <div className="text-[11px] text-tw-muted mt-1.5 font-mono">
                  De opname is bewaard — probeer opnieuw of typ de tekst hieronder in.
                </div>
              </div>
              <button
                type="button"
                onClick={retryUpload}
                disabled={!audio.blob}
                data-testid="upload-retry-btn"
                className="tw-press shrink-0 h-9 px-3 border border-tw-red text-tw-red hover:bg-tw-red hover:text-white uppercase tracking-[0.2em] text-[10px] font-bold flex items-center gap-1 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-tw-red focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A]"
              >
                <RefreshCw size={12} strokeWidth={2.5} /> Opnieuw
              </button>
            </div>
          )}

          {/* D3 — Whisper silence-hallucination guard: warn, never block. Raw transcript is untouched. */}
          {phase === "review" && transcriptWarning && (
            <div
              className="mb-6 border border-tw-yellow bg-tw-yellow/10 p-4 sm:p-5 flex items-start gap-3 tw-fadeup"
              data-testid="transcript-warning-banner"
            >
              <AlertTriangle size={20} className="shrink-0 mt-0.5 text-tw-yellow" />
              <div className="flex-1 min-w-0">
                <div className="font-bold uppercase tracking-wider text-tw-yellow text-sm mb-1">
                  Controleer het transcript
                </div>
                <div className="text-sm text-tw-muted break-words">
                  Let op: deze opname bevatte veel stilte — controleer het transcript.
                </div>
              </div>
            </div>
          )}

          {/* Review — fields section, visually separated from recording status */}
          {phase === "review" && (
            <section data-testid="review-fields" className="space-y-6 tw-fadeup">
              {/* Small "captured" acknowledgement */}
              <div className="flex items-center gap-3 pb-4 border-b border-tw" data-testid="review-meta">
                <div className="w-9 h-9 rounded-full bg-tw-yellow/10 border border-tw-yellow flex items-center justify-center shrink-0">
                  <Check size={16} className="text-tw-yellow" strokeWidth={3} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[9px] tracking-[0.3em] text-tw-yellow uppercase">
                    Opname klaar
                  </div>
                  <div className="font-mono text-xs text-tw-muted mt-0.5">
                    <span data-testid="record-duration">{formatDuration(duration)}</span>
                    {" · "}
                    {editedTranscript.split(/\s+/).filter(Boolean).length} woorden
                  </div>
                </div>
              </div>

              <div>
                <FieldLabel htmlFor="transcript">Transcript</FieldLabel>
                <div className="bg-tw-surface border-l-2 border-tw-yellow border-y border-r border-tw focus-within:border-l-tw-yellow transition-colors duration-200">
                  <Textarea
                    id="transcript"
                    value={editedTranscript}
                    onChange={(e) => setEditedTranscript(e.target.value)}
                    placeholder={
                      uploadError
                        ? "Typ hier je transcript, of druk op Opnieuw om nogmaals te transcriberen."
                        : "Je transcript verschijnt hier. Bewerk gerust."
                    }
                    data-testid="edit-transcript"
                    className="tw-reading bg-transparent border-0 text-white min-h-[220px] sm:min-h-[260px] rounded-none px-5 sm:px-7 py-5 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none"
                  />
                </div>
                <p className="mt-2 flex items-start gap-1.5 text-[11px] text-tw-muted leading-relaxed" data-testid="ai-enrich-hint">
                  <Sparkles size={12} strokeWidth={2.5} className="text-tw-yellow shrink-0 mt-0.5" />
                  <span>Na het opslaan maakt de AI automatisch een titel, onderwerpen, een samenvatting en de locatie bij deze memo.</span>
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <FieldLabel htmlFor="category-trigger">Categorie</FieldLabel>
                  {categorizing && !category && (
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] uppercase text-tw-muted" data-testid="category-suggesting">
                      <Loader2 size={10} className="animate-spin" /> AI kiest…
                    </span>
                  )}
                  {categoryIsSuggested && category && (
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] uppercase text-tw-yellow" data-testid="category-ai-suggested">
                      <Sparkles size={10} strokeWidth={2.5} /> AI-suggestie
                    </span>
                  )}
                </div>
                <Select
                  value={category}
                  onValueChange={(v) => { setCategory(v); setCategoryIsSuggested(false); }}
                >
                  <SelectTrigger
                    id="category-trigger"
                    data-testid="category-select"
                    className={`tw-press bg-tw-surface border-tw text-white h-12 rounded-none focus:ring-0 focus:ring-offset-0 focus:border-tw-yellow hover:border-tw-muted ${categoryIsSuggested && category ? "border-tw-yellow" : ""}`}
                  >
                    <SelectValue placeholder="Kies een categorie…" />
                  </SelectTrigger>
                  <SelectContent className="bg-tw-surface border-tw text-white rounded-none max-w-[min(92vw,28rem)] [&_[data-radix-select-viewport]]:w-full [&_[data-radix-select-viewport]]:min-w-0">
                    {categoryList.map((c) => (
                      <SelectPrimitive.Item
                        key={c.name}
                        value={c.name}
                        data-testid={`category-option-${c.name}`}
                        className="group relative flex w-full min-w-0 cursor-default select-none flex-col gap-1 rounded-none py-3 pl-3 pr-8 outline-none focus:bg-tw-yellow focus:text-black data-[highlighted]:bg-tw-yellow data-[highlighted]:text-black data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                      >
                        <span className="absolute right-2.5 top-3.5 flex h-3.5 w-3.5 items-center justify-center">
                          <SelectPrimitive.ItemIndicator>
                            <Check className="h-3.5 w-3.5" />
                          </SelectPrimitive.ItemIndicator>
                        </span>
                        {/* Only THIS ItemText content is portaled into the closed trigger — the
                            description below stays list-only, so the collapsed select never grows. */}
                        <SelectPrimitive.ItemText>
                          <span className="inline-flex items-center gap-2 uppercase tracking-wider text-sm">
                            <span className="inline-block w-1.5 h-1.5 shrink-0" style={{ backgroundColor: c.color }} aria-hidden />
                            {c.name}
                          </span>
                        </SelectPrimitive.ItemText>
                        {c.description && (
                          <span
                            className="line-clamp-2 w-full min-w-0 text-[11px] font-normal normal-case tracking-normal leading-snug text-tw-muted group-data-[highlighted]:text-black/70 pl-3.5"
                            data-testid={`category-option-desc-${c.name}`}
                          >
                            {c.description}
                          </span>
                        )}
                      </SelectPrimitive.Item>
                    ))}
                  </SelectContent>
                </Select>
                {category && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-tw-muted" data-testid="category-description">
                    <InfoPopover text={categoryList.find((c) => c.name === category)?.description} testId="category-info" placement="bottom" />
                    <span>Wat hoort hier?</span>
                  </p>
                )}
                {!categoriesLoading && categoryList.length === 0 && (
                  <p className="mt-1.5 flex items-center gap-2 text-[11px] text-tw-red" data-testid="category-load-error">
                    <span>Categorieën konden niet worden geladen.</span>
                    <button
                      type="button"
                      onClick={reloadCategories}
                      data-testid="category-reload-btn"
                      className="tw-press underline text-tw-yellow hover:text-white"
                    >
                      Opnieuw proberen
                    </button>
                  </p>
                )}
              </div>

              <div>
                <FieldLabel htmlFor="transform-trigger">Verwerk als</FieldLabel>
                <Select value={transform} onValueChange={setTransform}>
                  <SelectTrigger
                    id="transform-trigger"
                    data-testid="transform-select"
                    className="tw-press bg-tw-surface border-tw text-white h-12 rounded-none focus:ring-0 focus:ring-offset-0 focus:border-tw-yellow hover:border-tw-muted"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
                    {TRANSFORM_ORDER.map((t) => (
                      <SelectItem
                        key={t}
                        value={t}
                        data-testid={`transform-option-${t}`}
                        className="focus:bg-tw-yellow focus:text-black rounded-none text-sm"
                      >
                        {TRANSFORM_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {transform !== "raw" && (
                  <p className="mt-2 text-[11px] text-tw-muted font-mono tracking-wide">
                    De AI genereert dit na opslaan. Duurt tot ~30s.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setShowTransformInfo((v) => !v)}
                  data-testid="transform-info-toggle"
                  className="tw-press mt-2 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-tw-muted hover:text-tw-yellow"
                >
                  <Info size={12} strokeWidth={2.5} />
                  Wat betekenen deze keuzes?
                  {showTransformInfo ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>
                {showTransformInfo && (
                  <div className="mt-2 pl-3 border-l-2 border-tw-yellow/40 space-y-1.5 tw-fadeup" data-testid="transform-info-block">
                    <p className="text-[11px] text-tw-muted leading-relaxed"><span className="text-white">Alleen transcript</span> — alleen de uitgeschreven tekst, verder niets.</p>
                    <p className="text-[11px] text-tw-muted leading-relaxed"><span className="text-white">Samenvatting</span> — de kern in enkele zinnen (krijg je na het opslaan sowieso automatisch).</p>
                    <p className="text-[11px] text-tw-muted leading-relaxed"><span className="text-white">Bulletpoints</span> — feiten, beslissingen en acties puntsgewijs.</p>
                    <p className="text-[11px] text-tw-muted leading-relaxed"><span className="text-white">Werkinstructie (SOP)</span> — een genummerd stappenplan dat iemand zonder voorkennis kan volgen.</p>
                    <p className="text-[11px] text-tw-muted leading-relaxed"><span className="text-white">Kennisartikel</span> — een kort artikel met kopjes.</p>
                    <p className="text-[11px] text-tw-muted leading-relaxed pt-1 border-t border-tw">De verwerkte versie komt náást het ruwe transcript te staan en is terug te lezen in Mijn kluis en de Bibliotheek.</p>
                  </div>
                )}
              </div>

              <div className="space-y-3 pt-2">
                <ScoringHint text={editedTranscript} question={selectedQuestion} week={game.week} user={user} />
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !category || !editedTranscript.trim()}
                  data-testid="record-submit-btn"
                  className="tw-press w-full h-12 rounded-none bg-tw-yellow text-black hover:bg-[#FFDE33] uppercase tracking-[0.2em] text-sm font-bold disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A]"
                >
                  {submitting ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : processingLabel ? (
                    <span data-testid="processing-label" className="inline-flex items-center gap-2">
                      <Loader2 className="animate-spin" size={16} />
                      Wordt verwerkt…
                    </span>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" strokeWidth={2.5} />
                      Opslaan in mijn kluis
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleReRecord}
                  variant="outline"
                  disabled={submitting}
                  data-testid="record-rerecord-btn"
                  className="tw-press w-full h-12 rounded-none bg-transparent border-tw text-white hover:bg-tw-surface-hover hover:text-tw-yellow hover:border-tw-yellow uppercase tracking-[0.2em] text-sm focus-visible:ring-2 focus-visible:ring-tw-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A]"
                >
                  <RotateCcw size={16} className="mr-2" strokeWidth={2.5} />
                  Opnieuw inspreken
                </Button>
                <Button
                  onClick={handleReset}
                  variant="outline"
                  disabled={submitting}
                  data-testid="record-discard-btn"
                  className="tw-press w-full h-12 rounded-none bg-transparent border-transparent text-tw-muted hover:bg-tw-surface hover:text-tw-red hover:border-tw-red uppercase tracking-[0.2em] text-sm focus-visible:ring-2 focus-visible:ring-tw-red focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A]"
                >
                  <Trash2 size={16} className="mr-2" strokeWidth={2.5} />
                  Opname verwijderen
                </Button>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Sub-components ---------------- */

function ScreenHeader({ kicker, title, lead }) {
  return (
    <header className="mb-8 sm:mb-10 pb-6 border-b border-tw">
      <p className="font-mono text-[10px] tracking-[0.3em] text-tw-muted uppercase">
        {kicker}
      </p>
      <h1 className="mt-2 font-display text-3xl sm:text-5xl font-black text-white leading-none tracking-tight">
        {title}
      </h1>
      {lead && (
        <p className="mt-4 text-sm sm:text-[15px] text-tw-muted max-w-lg leading-relaxed">
          {lead}
        </p>
      )}
    </header>
  );
}

function CaptureHeader({ phase, duration, recording }) {
  const kicker =
    phase === "recording"
      ? "Opname actief"
      : phase === "uploading"
      ? "Opname verwerken"
      : phase === "review"
      ? "Opname klaar"
      : "Klaar om op te nemen";

  const title =
    phase === "recording" ? (
      <>
        Spreek. Wij <span className="text-tw-yellow">structureren.</span>
      </>
    ) : phase === "uploading" ? (
      <>
        Even <span className="text-tw-yellow">geduld…</span>
      </>
    ) : phase === "review" ? (
      <>
        Controleer en <span className="text-tw-yellow">sla op.</span>
      </>
    ) : (
      <>
        Klaar om <span className="text-tw-yellow">op te nemen.</span>
      </>
    );

  const lead =
    phase === "recording"
      ? "Vertel rustig door. Je kunt de tekst controleren voordat je iets deelt."
      : phase === "uploading"
      ? "We versturen de opname naar de server voor herkenning."
      : phase === "review"
      ? "Bewerk waar nodig, kies een categorie en sla op in je kluis."
      : "Kies je vraag hierboven en tik op de opnameknop.";

  return (
    <header className="mb-6 sm:mb-8 pb-5 border-b border-tw">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10px] tracking-[0.3em] text-tw-muted uppercase">
            {kicker}
          </p>
          <h1 className="mt-2 font-display text-2xl sm:text-4xl font-black text-white leading-none tracking-tight">
            {title}
          </h1>
        </div>
        {(phase === "recording" || phase === "review" || phase === "uploading") && (
          <span
            className={`shrink-0 font-mono tabular-nums text-lg sm:text-2xl font-medium ${
              recording ? "text-tw-red" : "text-white/80"
            }`}
            aria-label="Duur"
          >
            {formatDuration(duration)}
          </span>
        )}
      </div>
      <p className="mt-3 text-sm text-tw-muted max-w-lg leading-relaxed">{lead}</p>
    </header>
  );
}

function SelectedContext({ selectedQuestion, freeMode, phase, onChange }) {
  const { colorOf } = useCategories();
  if (!selectedQuestion && !freeMode) return null;
  const canChange = phase === "idle";
  const catColor = selectedQuestion?.category ? colorOf(selectedQuestion.category) : null;
  return (
    <section
      data-testid="selected-context"
      className="mb-6 bg-tw-surface border-l-2 border-tw-yellow border-y border-r border-tw px-4 sm:px-5 py-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow mb-2 uppercase">
            {freeMode ? "Vrij inspreken" : "Kennisvraag"}
          </div>
          {selectedQuestion ? (
            <>
              <p
                className="text-white text-[15px] sm:text-base leading-snug"
                data-testid="selected-question-text"
              >
                {selectedQuestion.text}
              </p>
              {selectedQuestion.category && (
                <span
                  className="inline-block mt-3 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] text-black"
                  style={{ backgroundColor: catColor }}
                >
                  {selectedQuestion.category}
                </span>
              )}
            </>
          ) : (
            <p className="text-white text-sm sm:text-base">
              Geen vraag — vertel wat er in je opkomt.
            </p>
          )}
        </div>
        {canChange && (
          <button
            type="button"
            onClick={onChange}
            data-testid="selected-change-btn"
            className="tw-press shrink-0 font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-1 border border-tw text-tw-muted hover:text-tw-yellow hover:border-tw-yellow"
            aria-label="Andere vraag kiezen"
            title="Wijzig"
          >
            Wijzig
          </button>
        )}
      </div>
    </section>
  );
}

function ServerModeNote({ compact = false }) {
  return (
    <div
      className={`flex items-center gap-2.5 border-l-2 border-tw-yellow bg-tw-surface/60 px-3 py-2 ${
        compact ? "mb-4" : "mb-6"
      } tw-fadeup`}
      data-testid="server-mode-badge"
    >
      <ServerCog size={14} className="text-tw-yellow shrink-0" strokeWidth={2.5} />
      <div className="text-xs text-tw-muted leading-snug">
        <span className="text-white font-medium">Server-transcriptie actief</span>
        {" — "}
        <span>de opname wordt na afloop verzonden voor tekst&shy;herkenning.</span>
      </div>
    </div>
  );
}

function MediaRecUnsupportedBanner() {
  return (
    <div
      className="mb-6 border border-tw-red bg-tw-red/10 text-tw-red p-4 flex items-start gap-3"
      data-testid="mediarec-unsupported"
    >
      <AlertTriangle size={20} className="shrink-0 mt-0.5" />
      <div className="text-sm leading-relaxed">
        <div className="font-bold uppercase tracking-wider mb-1">
          Audio-opname niet ondersteund
        </div>
        Deze browser kan geen audio opnemen. Gebruik een recente Chrome, Edge, Safari of Firefox.
      </div>
    </div>
  );
}

function Kicker({ children }) {
  return (
    <p className="font-mono text-[10px] tracking-[0.3em] text-tw-muted uppercase">
      {children}
    </p>
  );
}

function FieldLabel({ htmlFor, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block mb-2 font-mono text-[10px] tracking-[0.3em] text-tw-muted uppercase"
    >
      {children}
    </label>
  );
}

function CategoryChip({ name, compact = false }) {
  const { colorOf } = useCategories();
  const color = colorOf(name);
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-mono uppercase font-medium text-tw-muted ${
        compact ? "text-[10px] tracking-[0.2em]" : "text-[11px] tracking-[0.24em]"
      }`}
    >
      <span
        className="inline-block w-1.5 h-1.5"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      {name}
    </span>
  );
}

function LiveModeToggle({ enabled, onChange }) {
  return (
    <div
      className="mb-6 flex items-center justify-between gap-3 bg-tw-surface border border-tw px-3 py-2.5 tw-fadeup"
      data-testid="live-mode-toggle"
    >
      <div className="flex items-center gap-2 min-w-0">
        <AudioLines size={14} className={enabled ? "text-tw-yellow shrink-0" : "text-tw-muted shrink-0"} strokeWidth={2.5} />
        <div className="text-xs leading-snug min-w-0">
          <div className="text-white font-medium">Live meeschrijven</div>
          <div className="text-tw-muted text-[11px]">
            {enabled
              ? "Live tekst — kwaliteit hangt van je browser af. Server-transcriptie blijft strikter."
              : "Zie de tekst tijdens het spreken. Alleen op desktop Chrome / Edge."}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!enabled)}
        data-testid="live-mode-toggle-btn"
        role="switch"
        aria-checked={enabled}
        aria-label={enabled ? "Zet live meeschrijven uit" : "Zet live meeschrijven aan"}
        className={`shrink-0 w-11 h-6 border transition-colors relative ${
          enabled ? "bg-tw-yellow border-tw-yellow" : "bg-tw-black border-tw"
        }`}
      >
        <span
          className={`absolute top-[2px] w-4 h-4 transition-transform duration-150 ${
            enabled ? "translate-x-[22px] bg-black" : "translate-x-[2px] bg-white"
          }`}
          aria-hidden
        />
      </button>
    </div>
  );
}

